import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useApiFetch } from "../api/client";

export type AppRole = "Viewer" | "Uploader" | "Admin";

interface Me {
  appUserId: number;
  displayName: string;
  userPrincipalName: string;
  appRole: AppRole;
  environment: string;
}

interface AppUserValue {
  appUserId: number | null;
  appRole: AppRole | null;
  loading: boolean;
  refresh: () => void;
}

const AppUserContext = createContext<AppUserValue | null>(null);

// The source of truth for what a signed-in user may do is this app's own
// database (AppUser.AppRole), not the Entra token — Entra only proves who
// signed in. That means it's only known after an async /me round trip, not
// synchronously from the MSAL token the way Entra app roles used to be.
export function AppUserProvider({ children }: { children: ReactNode }) {
  const apiFetch = useApiFetch();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    apiFetch("me")
      .then((res) => res.json())
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <AppUserContext.Provider
      value={{ appUserId: me?.appUserId ?? null, appRole: me?.appRole ?? null, loading, refresh }}
    >
      {children}
    </AppUserContext.Provider>
  );
}

export function useAppUser(): AppUserValue {
  const ctx = useContext(AppUserContext);
  if (!ctx) throw new Error("useAppUser must be used within AppUserProvider");
  return ctx;
}
